import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowUp, MessageCircle, RotateCcw, X, CalendarDays, Users, Dog, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/common/Logo";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { fmtDate, guestsLabel, nightsLabel, nok } from "@/lib/format";
import { buildBookingUrl } from "@/lib/booking";
import { useConcierge } from "./ConciergeContext";
import { useIsMobile, useAnchorPassed } from "@/components/booking/BookingBar";

const SESSION_KEY = "sfh_chat_session";
const newSession = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderLite(text) {
  const lines = escapeHtml(text || "").split(/\n/);
  let html = "";
  let inList = false;
  for (const raw of lines) {
    const line = raw.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (/^\s*[-•]\s+/.test(line)) {
      if (!inList) { html += "<ul>"; inList = true; }
      html += `<li>${line.replace(/^\s*[-•]\s+/, "")}</li>`;
    } else {
      if (inList) { html += "</ul>"; inList = false; }
      if (line.trim()) html += `<p>${line}</p>`;
    }
  }
  if (inList) html += "</ul>";
  return html;
}

const QuoteCard = ({ action, onBook }) => {
  const { lang, tr, tx } = useLang();
  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-border bg-background" data-testid="fjordverten-quote-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border bg-secondary px-4 py-3 text-xs text-foreground">
        <span className="inline-flex items-center gap-1.5 font-semibold"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(action.check_in, lang)} → {fmtDate(action.check_out, lang)}</span>
        <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{guestsLabel(action.guests, lang)}</span>
        <span>{nightsLabel(action.night_count, lang)}</span>
        {action.dog && <span className="inline-flex items-center gap-1.5"><Dog className="h-3.5 w-3.5" />{tx("Hund", "Dog")}</span>}
      </div>
      <ul className="divide-y divide-border">
        {action.options.map((o) => (
          <li key={o.room_type_id} className="flex items-center gap-3 px-4 py-3">
            {o.image && <img src={o.image} alt="" className="h-11 w-11 shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{tr(o.name)}</p>
              <p className="text-xs text-muted-foreground">
                {o.available > 0 ? (o.available <= 3 ? tx(`Berre ${o.available} rom att`, `Only ${o.available} left`) : tx("Ledig", "Available")) : tx("Fullt desse datoane", "Sold out these dates")}
                {o.guests < action.guests && ` · ${tx("per rom", "per room")} (${o.guests})`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-primary tabular">{nok(o.subtotal, lang)}</p>
              <p className="text-[10px] text-muted-foreground">{nok(o.avg_per_night, lang)}/{tx("natt", "night")}</p>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
        <p className="text-[11px] text-muted-foreground">{tx("Prisoverslag · frukost inkl. · betal på hotellet", "Estimate · breakfast incl. · pay at hotel")}</p>
        <Button size="sm" onClick={() => onBook(action)} data-testid="fjordverten-quote-book-button">
          {tx("Bestill dette", "Book this")} <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export const FjordvertenWidget = () => {
  const { open, setOpen, pending, clearPending } = useConcierge();
  const { lang, tx } = useLang();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const ensureSession = useCallback(() => {
    let s = sessionId;
    if (!s) {
      s = newSession();
      localStorage.setItem(SESSION_KEY, s);
      setSessionId(s);
    }
    return s;
  }, [sessionId]);

  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    if (!sessionId) return;
    fetch(`${API}/concierge/session/${sessionId}`)
      .then((r) => r.json())
      .then((d) => setMessages((d.messages || []).map((m) => ({ role: m.role, content: m.content, action: m.action }))))
      .catch(() => {});
  }, [open, loaded, sessionId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    const sid = ensureSession();
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", streaming: true }]);
    const patchLast = (fn) => setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = fn(copy[copy.length - 1]);
      return copy;
    });
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: msg, lang }),
      });
      if (!res.ok || !res.body) throw new Error(res.status === 429 ? "rate" : "net");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const ev = JSON.parse(line.slice(5).trim());
          if (ev.type === "delta") patchLast((l) => ({ ...l, content: l.content + ev.text }));
          else if (ev.type === "action") patchLast((l) => ({ ...l, action: ev.action }));
          else if (ev.type === "error") throw new Error("llm");
        }
      }
      patchLast((l) => ({ ...l, streaming: false }));
    } catch (e) {
      patchLast((l) => ({
        ...l,
        streaming: false,
        error: true,
        content: e.message === "rate"
          ? tx("Du sender mange meldingar no – vent litt og prøv igjen.", "You're sending a lot of messages – please wait a moment.")
          : tx("Orsak, eg fekk ikkje svart akkurat no. Ring oss gjerne på 917 03 536.", "Sorry, I couldn't answer right now. Feel free to call +47 917 03 536."),
      }));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [input, busy, ensureSession, lang, tx]);

  useEffect(() => {
    if (open && pending) {
      const p = pending;
      clearPending();
      setTimeout(() => send(p), 150);
    }
  }, [open, pending, clearPending, send]);

  const reset = () => {
    const s = newSession();
    localStorage.setItem(SESSION_KEY, s);
    setSessionId(s);
    setMessages([]);
  };

  const onBook = (a) => {
    const cart = a.room_type_id ? [{ room_type_id: a.room_type_id, guests: Math.min(a.guests, a.options.find((o) => o.room_type_id === a.room_type_id)?.guests || a.guests), board: "standard" }] : [];
    setOpen(false);
    navigate(buildBookingUrl({ ci: a.check_in, co: a.check_out, adults: a.guests, dog: a.dog, cart, step: 2 }));
  };

  const chips = lang === "en"
    ? ["Can I bring my dog?", "When is breakfast?", "Price for 2 next weekend?", "How do I get there from Bergen?"]
    : ["Kan eg ta med hund?", "Når er det frukost?", "Kva kostar det for 2 neste helg?", "Korleis kjem eg meg dit frå Bergen?"];

  const hideFab = location.pathname.startsWith("/admin");
  const bottomBarVisible = useAnchorPassed({ whenMissing: true, offset: 64 });
  // On phones the FAB waits until the hero search card has scrolled away, so it never sits on top of it
  const fabHidden = hideFab || (isMobile && !bottomBarVisible);
  const fabBottom = isMobile && bottomBarVisible && !location.pathname.startsWith("/mi-bestilling") ? "bottom-[92px]" : "bottom-5";

  return (
    <>
      {!fabHidden && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="fjordverten-open"
          aria-label={tx("Opne Fjordverten", "Open Fjordverten")}
          className={`fixed right-4 sm:right-6 ${fabBottom} no-print z-40 inline-flex h-12 items-center gap-2.5 rounded-full bg-primary text-primary-foreground shadow-pop transition-[background-color,bottom,transform] duration-300 hover:bg-primary-hover active:scale-[0.98] ${isMobile ? "w-12 justify-center" : "pl-4 pr-5"}`}
        >
          <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
          {!isMobile && <span className="text-sm font-semibold">{tx("Spør Fjordverten", "Ask Fjordverten")}</span>}
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[460px] [&>button]:hidden" data-testid="fjordverten-panel">
          <div className="flex items-center gap-3 border-b border-border bg-card px-5 py-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary">
              <LogoMark className="h-[18px] text-white" title="" />
            </span>
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-lg font-bold tracking-tight text-foreground">Fjordverten</SheetTitle>
              <SheetDescription className="text-xs text-muted-foreground">{tx("Digital resepsjonist · svarar døgnet rundt", "Digital receptionist · here 24/7")}</SheetDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={reset} aria-label={tx("Ny samtale", "New chat")} data-testid="fjordverten-reset"><RotateCcw className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label={tx("Lukk", "Close")} data-testid="fjordverten-close"><X className="h-5 w-5" /></Button>
          </div>
          <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-6" data-testid="fjordverten-messages">
            <div className="max-w-[88%] rounded-2xl rounded-tl-md border border-border bg-card px-4 py-3 text-[0.92rem] leading-relaxed text-foreground">
              {tx(
                "Hei og velkomen! Eg er Fjordverten, den digitale resepsjonisten på Skånevik Fjordhotel. Spør meg om rom og prisar, mat, hund, parkering eller vegen hit – eg kan også lage eit prisoverslag.",
                "Hi and welcome! I'm Fjordverten, the digital receptionist at Skånevik Fjordhotel. Ask me about rooms and rates, dining, dogs, parking or directions – I can also prepare a price estimate."
              )}
            </div>
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2" data-testid="fjordverten-suggestions">
                {chips.map((c, i) => (
                  <button key={c} type="button" onClick={() => send(c)} data-testid={`fjordverten-chip-${i}`} className="rounded-full border border-border bg-card px-3.5 py-2 text-sm text-primary transition-colors hover:bg-secondary">
                    {c}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => m.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-primary px-4 py-2.5 text-[0.92rem] text-primary-foreground" data-testid="fjordverten-user-message">{m.content}</div>
              </div>
            ) : (
              <div key={i} className="max-w-[92%]" data-testid="fjordverten-assistant-message">
                <div className={`rounded-2xl rounded-tl-md border px-4 py-3 text-[0.92rem] leading-relaxed ${m.error ? "border-destructive/30 bg-card text-foreground" : "border-border bg-card text-foreground"}`}>
                  {m.content ? (
                    <div className={`chat-prose ${m.streaming ? "caret" : ""}`} dangerouslySetInnerHTML={{ __html: renderLite(m.content) }} />
                  ) : (
                    <span className="inline-flex gap-1 py-1" aria-label="…">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:-0.2s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60 [animation-delay:-0.1s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/60" />
                    </span>
                  )}
                </div>
                {m.action && m.action.type === "quote" && <QuoteCard action={m.action} onBook={onBook} />}
              </div>
            ))}
          </div>
          <form
            className="border-t border-border bg-card px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <div className="flex items-end gap-2 rounded-2xl border border-input bg-background px-3 py-2 focus-within:border-primary">
              <textarea
                ref={inputRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={tx("Skriv eit spørsmål…", "Ask a question…")}
                className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent py-2 text-[0.95rem] text-foreground outline-none placeholder:text-muted-foreground"
                maxLength={1000}
                data-testid="fjordverten-chat-input"
              />
              <Button type="submit" size="icon" className="h-10 w-10 rounded-xl" disabled={busy || !input.trim()} aria-label={tx("Send", "Send")} data-testid="fjordverten-send">
                <ArrowUp className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">{tx("Fjordverten er ein AI og kan ta feil. Bestillingar vert stadfesta av hotellet.", "Fjordverten is an AI and can make mistakes. Bookings are confirmed by the hotel.")}</p>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
};
